// 지그 결과 사진·STL 받기 공용 — 지그 메뉴(Jig.js)와 외주 의뢰 페이지(OutsourceSend.js, 2026-10-30)가 같이 쓴다.
//   사진 = 작업자가 찍은 PNG(jig-results, 7일 보관). 받기 = jig-results zip → STL 각각(대표 2026-10-22) + 접속기록(logJigAccess).
import React, { useState, useEffect } from 'react';
import { ref, getBlob, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { storage } from '../../firebase/config';

const photoUrlCache = new Map();
export function PhotoImg({ path, style, alt }) {
    const [url, setUrl] = useState(photoUrlCache.get(path) || '');
    useEffect(() => {
        let on = true;
        if (!path) return undefined;
        if (photoUrlCache.has(path)) { setUrl(photoUrlCache.get(path)); return undefined; }
        getDownloadURL(ref(storage, path))
            .then((u) => { photoUrlCache.set(path, u); if (on) setUrl(u); })
            .catch((e) => console.error('[사진] URL 실패(만료 가능):', e?.code || e?.message));
        return () => { on = false; };
    }, [path]);
    if (!url) return <span style={{ ...style, display: 'inline-block', background: '#f1f5f9' }} />;
    return <img src={url} alt={alt || ''} style={style} />;
}

export function safeName(r) {
    const base = [r.patient, r.clinic].filter(Boolean).join('_') || r.jobId || 'jig';
    return String(base).replace(/[\\/:*?"<>|]+/g, '_').slice(0, 80);
}

// zip Blob → STL 목록 [{name, buffer}] (단일 stl 이면 한 개). 여러 개면 각각 별도 파일로 받는다(대표 2026-10-22).
export async function zipToStlList(blob, ext) {
    if ((ext || 'zip') !== 'zip') return [{ name: 'jig.stl', buffer: await blob.arrayBuffer() }];
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(blob);
    const names = Object.keys(zip.files)
        .filter((n) => /\.stl$/i.test(n) && !zip.files[n].dir && !n.startsWith('__MACOSX'))
        .sort();
    if (names.length === 0) throw new Error('zip 안에 STL 이 없습니다');
    const out = [];
    for (const n of names) out.push({ name: n, buffer: await zip.files[n].async('arraybuffer') });
    return out;
}

/** STL 여러 개를 저장 — 한 개면 .stl 그대로, 둘 이상이면 zip 하나로 (대표 11-05 "상악 하악 같이 받게": 파일마다 클릭을 흉내 내면 브라우저가 두 번째부터 막아 하악만 받아졌다).
 *  items: [{label, buffer}] (label 은 확장자 없이). zipName: 묶음 이름(확장자 없이). */
export async function saveStlFiles(items, zipName) {
    const trigger = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    };
    if (items.length === 1) { trigger(new Blob([items[0].buffer], { type: 'model/stl' }), `${items[0].label}.stl`); return; }
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    items.forEach((it) => zip.file(`${it.label}.stl`, it.buffer));
    const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });   // STL 은 압축 효과가 적고 느리다 — 묶기만
    trigger(blob, `${zipName}.zip`);
}

/** 지그 결과 사진 목록 — [{label, path}] (상·하악이면 두 장) */
export function jigPhotos(r) {
    // 양악(치식에 1x·2x 와 3x·4x 가 같이)이면 1장째 상악·2장째 하악 (Jig.js photoList 와 같은 규칙, 대표 2026-10-29)
    const tt = (Array.isArray(r.teeth) ? r.teeth : String(r.teeth || '').split(/[,\s]+/)).map((t) => String(t).trim()).filter(Boolean);
    const both = tt.some((t) => /^[12]/.test(t)) && tt.some((t) => /^[34]/.test(t));
    const out = [];
    if (r.photoPath) out.push({ label: both ? '상악 지그' : '지그 옆', path: r.photoPath });
    if (r.comboPhotoPath) out.push({ label: both ? '하악 지그' : '지그 위', path: r.comboPhotoPath });
    return out;
}

/** 지그 결과 받기 — 파일 이름에 '지그'를 넣는다(대표 2026-10-28). 실패는 던진다(호출부가 화면에 알림) */
export async function downloadJigResult(r, labId) {
    const path = r.stlPath || `jig-results/${labId}/${r.jobId}.${r.resultExt || 'zip'}`;
    const blob = await getBlob(ref(storage, path));
    const list = await zipToStlList(blob, r.resultExt || 'zip');
    await saveStlFiles(list.map((s) => ({ label: list.length > 1 ? `${safeName(r)}_지그_${s.name.replace(/\.stl$/i, '')}` : `${safeName(r)}_지그`, buffer: s.buffer })), `${safeName(r)}_지그`);
    try { await httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'logJigAccess')({ jobId: r.jobId, action: 'download' }); }
    catch (e) { console.error('[지그] 접속기록 실패(다운로드는 계속):', e?.message || e); }
    return list.length;
}
